/**
 * MOLR exit-intent capture
 * ------------------------
 * Drives snippets/molr-capture.liquid. Fully progressive: if this file fails to
 * load, the panel simply never opens (it starts [hidden]); the footer and inline
 * newsletter captures still work. Nothing here is load-bearing.
 *
 * Rules honoured:
 *   - Skipped inside the theme editor.
 *   - NEVER a full-screen popup. On mobile the panel is a compact bottom
 *     slide-up (positioned entirely in CSS §35); this file only decides WHEN to
 *     open it, using a genuine "leaving" signal rather than covering the screen.
 *   - Shows at most once per session, and never again after a dismissal or a
 *     successful submit (sessionStorage flag).
 *   - prefers-reduced-motion: the slide/fade is dropped in CSS; opening still
 *     works, the panel is simply present.
 *   - Desktop trigger = cursor leaving the TOP of the viewport toward the
 *     browser chrome. Mobile trigger = scrolled into the page, then a decisive
 *     scroll back up (a leaving gesture), after a minimum dwell.
 */
(() => {
  'use strict';

  if (document.documentElement.classList.contains('shopify-design-mode')) return;

  const root = document.querySelector('[data-molr-capture]');
  if (!root) return;

  const panel = root.querySelector('.molr-capture__panel');
  const SEEN_KEY = 'molr-capture-seen';

  /** Session guard — read defensively (private mode / disabled storage). */
  function alreadySeen() {
    try {
      return sessionStorage.getItem(SEEN_KEY) === '1';
    } catch {
      return seenFallback;
    }
  }
  let seenFallback = false;
  function markSeen() {
    seenFallback = true;
    try {
      sessionStorage.setItem(SEEN_KEY, '1');
    } catch {
      /* storage unavailable — the in-memory fallback still holds for this page */
    }
  }

  const desktop = window.matchMedia('(min-width: 750px) and (pointer: fine)');

  let isOpen = false;
  let previouslyFocused = null;

  function open() {
    if (isOpen || alreadySeen()) return;
    isOpen = true;
    markSeen(); // once per session, before anything else

    previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    root.hidden = false;
    // Next frame so the [hidden]→visible change and the open class are separate
    // style resolutions — lets the CSS transition run from its start state.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => root.classList.add('molr-capture--open'));
    });

    document.addEventListener('keydown', onKeydown);
    teardownTriggers();

    // Move focus to the panel so keyboard and screen-reader users can act on it
    // and reach the close control. Non-modal: the page stays usable behind it.
    if (panel) panel.focus({ preventScroll: true });
  }

  function close() {
    if (!isOpen) return;
    isOpen = false;

    root.classList.remove('molr-capture--open');
    document.removeEventListener('keydown', onKeydown);

    const finish = () => {
      root.hidden = true;
      panel?.removeEventListener('transitionend', onEnd);
    };
    const onEnd = (event) => {
      if (event.target === panel && event.propertyName === 'transform') finish();
    };

    // If motion is reduced there may be no transition to wait on — hide next tick.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !panel) {
      window.setTimeout(finish, 0);
    } else {
      panel.addEventListener('transitionend', onEnd);
      window.setTimeout(finish, 600); // safety net if transitionend is missed
    }

    if (previouslyFocused && document.contains(previouslyFocused)) {
      previouslyFocused.focus({ preventScroll: true });
    }
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  }

  // Dismiss control(s) inside the panel.
  root.addEventListener('click', (event) => {
    if (event.target instanceof Element && event.target.closest('[data-molr-capture-dismiss]')) {
      close();
    }
  });

  // A submit is a conversion — mark seen so the reload doesn't reopen it, and
  // let the native form post (creates the Shopify customer).
  root.addEventListener('submit', () => markSeen());

  /* ---- Triggers ---------------------------------------------------------- */

  const MIN_DWELL_MS = 5000; // don't pounce the instant the page loads
  const loadedAt = Date.now();
  let triggersLive = false;

  function dwellPassed() {
    return Date.now() - loadedAt >= MIN_DWELL_MS;
  }

  // Desktop: cursor leaves toward the top chrome (tabs / address bar).
  function onMouseOut(event) {
    if (!dwellPassed()) return;
    if (event.clientY > 0) return; // only the top edge counts as "leaving"
    // relatedTarget/toElement null means the pointer left the document entirely.
    if (event.relatedTarget || event.toElement) return;
    open();
  }

  // Mobile: scrolled meaningfully into the page, then a decisive scroll back up.
  let maxScroll = 0;
  let upAccum = 0;
  let lastY = window.scrollY;
  function onScroll() {
    if (!dwellPassed()) {
      lastY = window.scrollY;
      return;
    }
    const y = window.scrollY;
    maxScroll = Math.max(maxScroll, y);

    const delta = y - lastY;
    lastY = y;

    // Require the visitor to have gone at least ~1.2 screens down first, so this
    // is a genuine "was reading, now leaving" gesture, not an early wobble.
    const enteredDeep = maxScroll > window.innerHeight * 1.2;
    if (!enteredDeep) return;

    if (delta < 0) {
      upAccum += -delta;
      if (upAccum > 320) open();
    } else if (delta > 0) {
      upAccum = 0; // reset on any downward movement
    }
  }

  function setupTriggers() {
    if (triggersLive || alreadySeen()) return;
    triggersLive = true;
    if (desktop.matches) {
      document.addEventListener('mouseout', onMouseOut);
    } else {
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  function teardownTriggers() {
    triggersLive = false;
    document.removeEventListener('mouseout', onMouseOut);
    window.removeEventListener('scroll', onScroll);
  }

  // Re-evaluate which trigger applies if the input model changes (e.g. desktop
  // window resized narrow, or a 2-in-1 switches to touch) — but never after the
  // panel has been seen.
  desktop.addEventListener('change', () => {
    if (alreadySeen() || isOpen) return;
    teardownTriggers();
    setupTriggers();
  });

  if (!alreadySeen()) setupTriggers();
})();
